'use client';

import type React from 'react';

import DefaultButton from '@/components/common/default/DefaultButton';
import { Check } from 'lucide-react';
import { memo, useCallback } from 'react';

interface FilterItemProps {
  label: string;
  count?: number;
  isSelected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

const FilterItem = memo<FilterItemProps>(
  ({
    label,
    count,
    isSelected = false,
    onClick,
    disabled = false,
    className = '',
    'aria-label': ariaLabel,
  }) => {
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if ((event.key === 'Enter' || event.key === ' ') && onClick && !disabled) {
          event.preventDefault();
          onClick();
        }
      },
      [onClick, disabled]
    );

    return (
      <DefaultButton
        type="button"
        onClick={onClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`tw-group tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-px-2 tw-py-2 tw-text-left tw-text-sm tw-transition-all tw-duration-200 hover:tw-bg-platinum-gray/20 focus:tw-bg-slate-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-slate-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${
          isSelected ? 'tw-bg-blue-50 tw-text-blue-900' : 'tw-text-slate-gray'
        } ${className}`}
        aria-label={
          ariaLabel ||
          `${isSelected ? 'Remove' : 'Add'} ${label} filter${count ? ` (${count} items)` : ''}`
        }
        aria-pressed={isSelected}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <div
            className={`tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded tw-border-2 !tw-border-solid tw-transition-all tw-duration-200 ${
              isSelected
                ? 'tw-border-blue-500 tw-bg-blue-500 tw-text-white'
                : 'tw-border-platinum-gray tw-bg-white group-hover:tw-border-cool-gray group-hover:tw-shadow-sm'
            }`}
          >
            {isSelected && <Check className="tw-h-3 tw-w-3" />}
          </div>
          <span className="tw-flex-1 tw-truncate tw-font-medium">{label}</span>
        </div>
        {count !== undefined && (
          <span
            className={`tw-ml-2 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-transition-colors ${
              isSelected
                ? 'tw-bg-blue-100 tw-text-blue-800'
                : 'tw-bg-platinum-gray/50 tw-text-slate-gray group-hover:tw-bg-platinum-gray'
            }`}
          >
            {count.toLocaleString()}
          </span>
        )}
      </DefaultButton>
    );
  }
);

FilterItem.displayName = 'FilterItem';

export default FilterItem;
